import React from "react";
import { UserCheck, Ban, Undo2, Trash2 } from "lucide-react";
import { adminUpdateUserRole, adminBanUser, adminUnbanUser, adminDeleteUser } from "../../lib/api";
import { toast } from "sonner";

export function UsersTable({ users, onImpersonate, onReload }) {
    if (!users.length) {
        return (
            <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-[#2A3655] rounded-sm">
                No users match.
            </div>
        );
    }
    const handleRoleChange = async (u, role) => {
        try {
            await adminUpdateUserRole(u.user_id, role);
            toast.success(`Role set to ${role}`);
            onReload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update role.");
        }
    };
    const handleBanToggle = async (u) => {
        try {
            if (u.banned) {
                await adminUnbanUser(u.user_id);
                toast.success("Unbanned");
            } else {
                // eslint-disable-next-line no-alert
                if (!window.confirm(`Ban ${u.email}? This blocks login + re-registration for this email AND their last IP.`)) return;
                await adminBanUser(u.user_id, { ban_ip: true });
                toast.success("Banned");
            }
            onReload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update ban.");
        }
    };
    const handleDelete = async (u) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Delete ${u.email}? This PERMANENTLY removes the user and all their projects, analyses, estimates, uploads, and prompts.`)) return;
        try {
            await adminDeleteUser(u.user_id);
            toast.success("User deleted");
            onReload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't delete.");
        }
    };
    return (
        <div
            className="overflow-x-auto overscroll-x-contain rounded-sm border border-[#2A3655]"
            style={{ touchAction: "pan-x pan-y", WebkitOverflowScrolling: "touch" }}
            data-testid="admin-users-table-scroll"
        >
            <table
                className="min-w-[980px] w-full text-[13px]"
                data-testid="admin-users-table"
            >
                <thead className="bg-[#0F1B3A] text-[10px] uppercase tracking-wider text-slate-400">
                    <tr>
                        <th className="text-left px-3 py-2">Email</th>
                        <th className="text-left px-3 py-2">Name</th>
                        <th className="text-left px-3 py-2">Company</th>
                        <th className="text-left px-3 py-2">Role</th>
                        <th className="text-right px-3 py-2">Projects</th>
                        <th className="text-right px-3 py-2">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[#1C253C]">
                    {users.map((u) => (
                        <tr
                            key={u.user_id}
                            data-testid={`admin-user-row-${u.user_id}`}
                            className="hover:bg-[#1C253C]"
                        >
                            <td className="px-3 py-2 text-white whitespace-nowrap">
                                {u.email || "—"}
                                {u.banned && (
                                    <span className="ml-2 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border border-red-500/60 text-red-300 bg-red-500/10">
                                        Banned
                                    </span>
                                )}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{u.full_name || "—"}</td>
                            <td className="px-3 py-2 text-slate-300 whitespace-nowrap">
                                {u.company || "—"}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                                <select
                                    value={u.role || "user"}
                                    onChange={(e) => handleRoleChange(u, e.target.value)}
                                    disabled={u.role === "superadmin"}
                                    data-testid={`admin-role-${u.user_id}`}
                                    className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border whitespace-nowrap bg-transparent cursor-pointer ${
                                        u.role === "superadmin"
                                            ? "border-rose-500/60 text-rose-300 bg-rose-500/10 cursor-not-allowed"
                                            : u.role === "admin"
                                                ? "border-[#00A3FF]/60 text-[#6EB2FF] bg-[#00A3FF]/10"
                                                : "border-slate-600 text-slate-300 bg-slate-800/50"
                                    }`}
                                >
                                    <option value="user">user</option>
                                    <option value="admin">admin</option>
                                    {u.role === "superadmin" && (
                                        <option value="superadmin">superadmin</option>
                                    )}
                                </select>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                                {u.project_count}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                <div className="inline-flex gap-1">
                                    <button
                                        type="button"
                                        onClick={() => onImpersonate(u)}
                                        disabled={u.role === "superadmin"}
                                        data-testid={`admin-impersonate-${u.user_id}`}
                                        title={
                                            u.role === "superadmin"
                                                ? "Can't impersonate other superadmins"
                                                : `Sign in as ${u.email}`
                                        }
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#2A3655] text-[11px] font-bold text-slate-200 hover:bg-[#007AFF]/15 hover:border-[#00A3FF] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <UserCheck className="w-3 h-3" />
                                        Impersonate
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleBanToggle(u)}
                                        disabled={u.role === "superadmin"}
                                        data-testid={`admin-ban-${u.user_id}`}
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#2A3655] text-[11px] font-bold text-amber-300 hover:bg-amber-500/10 hover:border-amber-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        {u.banned ? <Undo2 className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
                                        {u.banned ? "Unban" : "Ban"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleDelete(u)}
                                        disabled={u.role === "superadmin"}
                                        data-testid={`admin-delete-${u.user_id}`}
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-red-600/40 text-[11px] font-bold text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                        Delete
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
